import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback, useRef } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute, CognitoRefreshToken } from 'amazon-cognito-identity-js';
import { setApiToken, setRefreshHandler } from '../api/client';

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
});

interface User {
  email: string;
  sub: string;
}

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isReady: boolean;
  user: User | null;
  error: Error | undefined;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  confirmSignup: (email: string, code: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmNewPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function parseJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now() + 60_000;
}

function tokenExpiresWithin(token: string, ms: number): boolean {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now() + ms;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAuthenticated = !!token;

  const storeSession = useCallback((accessToken: string, idToken: string, refreshToken?: string) => {
    localStorage.setItem('cognito_access_token', accessToken);
    localStorage.setItem('cognito_id_token', idToken);
    if (refreshToken) {
      localStorage.setItem('cognito_refresh_token', refreshToken);
    }
    setToken(accessToken);
    setApiToken(accessToken);
    const idPayload = parseJwt(idToken);
    if (idPayload) {
      setUser({ email: idPayload.email, sub: idPayload.sub });
    }
  }, []);

  const clearLocalSession = useCallback(() => {
    setToken(null);
    setUser(null);
    setApiToken(null);
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_refresh_token');
  }, []);

  const refreshAccessToken = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      const storedRefreshToken = localStorage.getItem('cognito_refresh_token');
      const storedIdToken = localStorage.getItem('cognito_id_token');
      if (!storedRefreshToken || !storedIdToken) {
        reject(new Error('No refresh token available'));
        return;
      }

      const idPayload = parseJwt(storedIdToken);
      if (!idPayload?.sub) {
        reject(new Error('Cannot determine user from stored tokens'));
        return;
      }

      const cognitoUser = new CognitoUser({
        Username: idPayload.email || idPayload.sub,
        Pool: userPool,
      });
      const refreshToken = new CognitoRefreshToken({ RefreshToken: storedRefreshToken });

      cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          console.error('[auth] Token refresh failed:', err);
          reject(err);
          return;
        }
        const newAccessToken = session.getAccessToken().getJwtToken();
        const newIdToken = session.getIdToken().getJwtToken();
        storeSession(newAccessToken, newIdToken);
        console.log('[auth] Token refreshed successfully');
        resolve();
      });
    });
  }, [storeSession]);

  // Register refresh handler with API client and start proactive refresh timer
  useEffect(() => {
    setRefreshHandler(refreshAccessToken);

    refreshTimerRef.current = setInterval(() => {
      const currentToken = localStorage.getItem('cognito_access_token');
      if (currentToken && tokenExpiresWithin(currentToken, 5 * 60 * 1000)) {
        console.log('[auth] Proactive refresh: token expires within 5min');
        refreshAccessToken().catch((err) => {
          console.error('[auth] Proactive refresh failed:', err);
        });
      }
    }, 5 * 60 * 1000);

    return () => {
      setRefreshHandler(null);
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [refreshAccessToken]);

  // Restore session from localStorage on mount
  useEffect(() => {
    const restore = async () => {
      try {
        const storedToken = localStorage.getItem('cognito_access_token');
        const storedIdToken = localStorage.getItem('cognito_id_token');
        const storedRefreshToken = localStorage.getItem('cognito_refresh_token');

        if (storedToken && !isTokenExpired(storedToken)) {
          setToken(storedToken);
          setApiToken(storedToken);
          if (storedIdToken) {
            const idPayload = parseJwt(storedIdToken);
            if (idPayload) {
              setUser({ email: idPayload.email, sub: idPayload.sub });
            }
          }
          console.log('[auth] Session restored from storage');
        } else if (storedToken && storedRefreshToken) {
          // Access token expired but refresh token exists — try refresh
          console.log('[auth] Access token expired, attempting refresh');
          try {
            await refreshAccessToken();
            console.log('[auth] Session restored via refresh');
          } catch {
            console.log('[auth] Refresh failed on mount, clearing session');
            clearLocalSession();
          }
        } else if (storedToken) {
          clearLocalSession();
          console.log('[auth] Stored token expired, no refresh token, cleared');
        }
      } catch (e) {
        console.error('[auth] Failed to restore session:', e);
        setError(e instanceof Error ? e : new Error('Session restore failed'));
      }
      setIsLoading(false);
      setIsReady(true);
    };
    restore();
  }, [refreshAccessToken, clearLocalSession]);

  const login = useCallback(async (email: string, password: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    return new Promise<void>((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          const refreshToken = result.getRefreshToken().getToken();
          storeSession(accessToken, idToken, refreshToken);
          console.log('[auth] Login successful');
          resolve();
        },
        onFailure: (err) => {
          console.error('[auth] Login failed:', err);
          reject(err);
        },
      });
    });
  }, [storeSession]);

  const signup = useCallback(async (email: string, password: string) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
    ];
    return new Promise<void>((resolve, reject) => {
      userPool.signUp(email, password, attributes, [], (err) => {
        if (err) {
          console.error('[auth] Signup failed:', err);
          reject(err);
          return;
        }
        console.log('[auth] Signup successful, confirmation needed');
        resolve();
      });
    });
  }, []);

  const confirmSignup = useCallback(async (email: string, code: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) {
          console.error('[auth] Confirmation failed:', err);
          reject(err);
          return;
        }
        console.log('[auth] Email confirmed');
        resolve();
      });
    });
  }, []);

  const resendConfirmation = useCallback(async (email: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }, []);

  const confirmNewPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          console.log('[auth] Password reset successful');
          resolve();
        },
        onFailure: (err) => {
          console.error('[auth] Password reset failed:', err);
          reject(err);
        },
      });
    });
  }, []);

  const logout = useCallback(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.globalSignOut({
        onSuccess: () => {
          console.log('[auth] Global sign out successful');
        },
        onFailure: (err) => {
          console.error('[auth] Global sign out failed:', err);
        },
      });
    }
    // Clear local state synchronously regardless of globalSignOut result
    clearLocalSession();
  }, [clearLocalSession]);

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        isLoading,
        isReady,
        user,
        error,
        login,
        signup,
        confirmSignup,
        resendConfirmation,
        forgotPassword,
        confirmNewPassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
